import React, { useState, useMemo } from 'react';
import { X, IndianRupee, Banknote, Link as LinkIcon, QrCode, CheckCircle2, Copy, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { api } from '../services/api';

/**
 * 3-step modal:
 *   Step 1 (Review): Booking list + amount selector (full / custom)
 *   Step 2 (Collect): Choose payment method (Cash / Razorpay Link / QR)
 *   Step 3 (Result): Confirmation + breakdown (for cash) or share link/QR (online)
 */
const MonthlyResolveModal = ({ open, onClose, customer, month, onSettled }) => {
  const totalPending = useMemo(
    () => Number(customer?.total_pending || 0),
    [customer]
  );

  const [step, setStep] = useState(1);
  const [amountMode, setAmountMode] = useState('full'); // 'full' | 'custom'
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('offline'); // 'offline' | 'online_link' | 'qr_code'
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!open || !customer) return null;

  const amount = amountMode === 'full'
    ? totalPending
    : Math.max(0, Math.min(totalPending, parseFloat(customAmount) || 0));

  const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

  const reset = () => {
    setStep(1); setAmountMode('full'); setCustomAmount('');
    setPaymentMethod('offline'); setSubmitting(false); setResult(null); setError('');
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const submit = async () => {
    setError('');
    if (amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    if (amount > totalPending + 0.005) {
      setError(`Amount cannot exceed total pending ${fmt(totalPending)}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.request('/bookings/monthly-resolve', {
        method: 'POST',
        body: JSON.stringify({
          customer_phone: customer.customer_phone,
          facility_id: customer.facility_id,
          month,
          payment_method: paymentMethod,
          amount_collected: amount,
        }),
      });
      setResult(res);
      setStep(3);
      if (paymentMethod === 'offline' && res?.success) {
        toast({ title: 'Payment recorded', description: `${fmt(amount)} settled across ${res.breakdown?.length || 0} booking(s).` });
        onSettled?.();
      } else if (res?.success) {
        toast({ title: paymentMethod === 'qr_code' ? 'QR generated' : 'Payment link created', description: `Valid for 3 days.` });
        onSettled?.();
      }
    } catch (e) {
      setError(e?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: label });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-2 sm:p-4"
      data-testid="monthly-resolve-modal"
    >
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Resolve Monthly Payment</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {customer.customer_name || 'Customer'} · {customer.customer_phone} · {month}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-gray-100"
            data-testid="monthly-resolve-close"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        {/* Step pills */}
        <div className="px-4 sm:px-6 py-2 border-b bg-gray-50 flex gap-2 text-xs">
          {[
            { k: 1, label: 'Review' },
            { k: 2, label: 'Collect' },
            { k: 3, label: 'Result' },
          ].map((s) => (
            <span
              key={s.k}
              className={`px-2.5 py-1 rounded-full font-medium ${
                step === s.k
                  ? 'bg-blue-600 text-white'
                  : step > s.k
                  ? 'bg-green-100 text-green-700'
                  : 'bg-white border text-gray-500'
              }`}
            >
              {step > s.k ? '✓ ' : `${s.k}. `}{s.label}
            </span>
          ))}
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                <div className="text-xs text-gray-600">Total Pending for {month}</div>
                <div className="text-2xl font-bold text-blue-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />{totalPending.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">{customer.booking_count} booking(s)</div>
              </div>

              {/* Booking list */}
              <div className="rounded-lg border overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 grid grid-cols-12 gap-1">
                  <div className="col-span-4">Date</div>
                  <div className="col-span-3">Time</div>
                  <div className="col-span-2">Court</div>
                  <div className="col-span-3 text-right">Pending</div>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y">
                  {(customer.bookings || []).map((b) => (
                    <div
                      key={b.booking_id}
                      className="px-3 py-1.5 text-xs grid grid-cols-12 gap-1 hover:bg-gray-50"
                      data-testid={`monthly-booking-row-${b.booking_id}`}
                    >
                      <div className="col-span-4 text-gray-700">{b.date}</div>
                      <div className="col-span-3 text-gray-600">{b.start_time}–{b.end_time}</div>
                      <div className="col-span-2 text-gray-600 truncate">{b.court || '—'}</div>
                      <div className="col-span-3 text-right font-medium text-gray-900">{fmt(b.amount_pending)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amount selector */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Amount to collect</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setAmountMode('full'); setCustomAmount(''); }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${
                      amountMode === 'full' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid="monthly-resolve-amount-full"
                  >
                    Full pending ({fmt(totalPending)})
                  </button>
                  <button
                    type="button"
                    onClick={() => setAmountMode('custom')}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${
                      amountMode === 'custom' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid="monthly-resolve-amount-custom"
                  >
                    Custom amount
                  </button>
                </div>
                {amountMode === 'custom' && (
                  <div className="relative">
                    <IndianRupee className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max={totalPending}
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder={`1 – ${totalPending.toFixed(2)}`}
                      className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="monthly-resolve-custom-amount-input"
                    />
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Partial amounts will be applied in chronological order (oldest pending booking first).
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />{error}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Collecting <span className="font-bold text-gray-900">{fmt(amount)}</span> for {customer.customer_name || 'customer'}.
              </div>

              <div className="grid gap-2">
                {[
                  {
                    k: 'offline',
                    icon: Banknote,
                    title: 'Cash / Offline',
                    sub: 'Mark payment as received in person. Distributes instantly.',
                  },
                  {
                    k: 'online_link',
                    icon: LinkIcon,
                    title: 'Razorpay Payment Link',
                    sub: 'Send a payment link via SMS/WhatsApp. Expires in 3 days.',
                  },
                  {
                    k: 'qr_code',
                    icon: QrCode,
                    title: 'Razorpay UPI QR',
                    sub: 'Generate a QR code for in-venue scan. Expires in 3 days.',
                  },
                ].map(({ k, icon: Icon, title, sub }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setPaymentMethod(k)}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                      paymentMethod === k
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`monthly-method-${k}`}
                  >
                    <div className={`p-2 rounded-lg ${paymentMethod === k ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Icon className={`h-4 w-4 ${paymentMethod === k ? 'text-blue-700' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900">{title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
                    </div>
                  </button>
                ))}
              </div>

              {error && (
                <div className="text-xs text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />{error}
                </div>
              )}
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex flex-col items-center text-center py-2">
                <div className="p-3 bg-green-100 rounded-full mb-2">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-base font-bold text-gray-900">
                  {result.method === 'offline'
                    ? 'Payment Recorded'
                    : result.method === 'online_link'
                    ? 'Payment Link Created'
                    : 'QR Code Generated'}
                </div>
                <div className="text-xs text-gray-500 mt-1">Resolution ID: <span className="font-mono">{result.resolution_id}</span></div>
              </div>

              {result.method === 'offline' && (
                <div className="rounded-lg border overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 grid grid-cols-12 gap-1">
                    <div className="col-span-4">Date</div>
                    <div className="col-span-3">Time</div>
                    <div className="col-span-2 text-right">Credit</div>
                    <div className="col-span-3 text-right">Status</div>
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y">
                    {(result.breakdown || []).map((b) => (
                      <div key={b.booking_id} className="px-3 py-1.5 text-xs grid grid-cols-12 gap-1">
                        <div className="col-span-4 text-gray-700">{b.date}</div>
                        <div className="col-span-3 text-gray-600">{b.start_time}</div>
                        <div className="col-span-2 text-right font-medium text-green-700">+{fmt(b.credited)}</div>
                        <div className="col-span-3 text-right">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>{b.payment_status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.method === 'online_link' && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Payment Link</label>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={result.short_url || ''}
                      className="flex-1 px-3 py-2 border rounded-lg text-xs font-mono bg-gray-50"
                      data-testid="monthly-resolve-link-url"
                    />
                    <Button
                      variant="outline" size="sm"
                      onClick={() => copy(result.short_url, 'Link copied')}
                      data-testid="monthly-resolve-link-copy"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => window.open(result.short_url, '_blank')}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500">
                    The link has been sent via SMS to <span className="font-medium">{result.customer_phone}</span>. It will expire in 3 days. Once paid, all selected bookings auto-update.
                  </div>
                </div>
              )}

              {result.method === 'qr_code' && (
                <div className="space-y-2">
                  <div className="flex flex-col items-center">
                    {result.qr_image_url && (
                      <img
                        src={result.qr_image_url}
                        alt="UPI QR"
                        className="w-56 h-56 object-contain border rounded-lg p-2 bg-white"
                        data-testid="monthly-resolve-qr-image"
                      />
                    )}
                    <div className="text-sm font-bold text-gray-900 mt-2">{fmt(amount)}</div>
                    <div className="text-xs text-gray-500">Single-use · Valid 3 days</div>
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    Customer scans this with any UPI app. Bookings auto-settle once payment is received.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 flex justify-between items-center">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose} data-testid="monthly-resolve-cancel">Cancel</Button>
              <Button
                onClick={() => { setError(''); if (amount <= 0) { setError('Amount must be > 0'); return; } setStep(2); }}
                data-testid="monthly-resolve-continue"
              >
                Continue → ({fmt(amount)})
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="monthly-resolve-submit"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {paymentMethod === 'offline' ? 'Record Payment' : paymentMethod === 'online_link' ? 'Create Link' : 'Generate QR'}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <div className="text-xs text-gray-500">
                {result?.method === 'offline' ? 'Bookings updated.' : 'Awaiting customer payment.'}
              </div>
              <Button onClick={handleClose} data-testid="monthly-resolve-done">Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyResolveModal;
